import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { AsyncPipe, NgForOf } from '@angular/common';
import {
  TuiDataListModule,
  TuiLoaderModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { TuiSelectModule } from '@taiga-ui/kit';
import { AuthState } from '@store/auth/auth.state';
import { Store } from '@ngxs/store';
import { TuiContextWithImplicit, TuiStringHandler } from '@taiga-ui/cdk';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@shared/services/auth.service';
import {
  BehaviorSubject,
  catchError,
  of,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { RefreshAuth } from '@store/auth/auth.actions';
import { AlertService } from '@shared/services/alert.service';
import { UserDivision } from '../../../types/auth';
import { RouteNames } from '../../../route-names';

@Component({
  selector: 'app-division-select',
  standalone: true,
  imports: [
    NgForOf,
    TuiDataListModule,
    TuiSelectModule,
    TuiTextfieldControllerModule,
    FormsModule,
    TuiLoaderModule,
    AsyncPipe,
  ],
  templateUrl: './division-select.component.html',
  styleUrl: './division-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DivisionSelectComponent implements OnInit {
  userDivisions = this.store.selectSignal(AuthState.getUserDivisions);
  user = this.store.selectSignal(AuthState.getUser);

  loading$ = new BehaviorSubject(false);
  changeDivision$ = new Subject<number>();
  refreshToken$ = this.store.select(AuthState.getRefreshToken);
  constructor(
    private readonly store: Store,
    private readonly auth: AuthService,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
    private readonly alert: AlertService,
  ) {}

  stringify(
    items: readonly UserDivision[] | null,
  ): TuiStringHandler<TuiContextWithImplicit<number>> {
    const arr = items?.map(({ id, name }) => [id, name] as [number, string]);
    const map = new Map(arr);
    return ({ $implicit }: TuiContextWithImplicit<number>) =>
      map.get($implicit) || '';
  }

  ngOnInit() {
    this.changeDivision$
      .pipe(
        tap(() => this.loading$.next(true)),
        switchMap(id => this.auth.changeActiveDivision(id)),
        withLatestFrom(this.refreshToken$),
        switchMap(([, refreshToken]) =>
          this.auth
            .refreshToken(refreshToken || '')
            .pipe(
              tap(authData => this.store.dispatch(new RefreshAuth(authData))),
            ),
        ),
        tap(() => {
          this.loading$.next(false);
          this.router.navigate([RouteNames.KANBAN_PAGE]);
        }),
        catchError(() => {
          this.alert.showError('Произошла ошибка при смене компании');
          this.loading$.next(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
